import * as THREE from 'three';
import { WeatherType } from './voxelTypes';
import { VOXEL_SIZE } from './VoxelInstanceBuffer';

/**
 * How hard it is raining.
 *
 * Rain and storm share one instanced mesh — a second one would be a second draw call for the
 * same drops — so the difference between a shower and a downpour is entirely in these numbers,
 * applied to the material and the active instance count as the weather changes.
 *
 * The gentle profile is not just "fewer drops": the storm's near-metal, low-roughness drop is
 * almost black under an overcast sky, which reads as heavy and is why it suits the storm. A
 * shower wants the opposite — a dull, pale drop that catches the daylight it is falling through.
 */
type RainProfile = {
  /** Fraction of the drop pool in the air at once. */
  density: number;
  /** Horizontal lean, in radians of drop tilt and as a share of fall speed. */
  tilt: number;
  color: number;
  opacity: number;
  metalness: number;
  roughness: number;
  /** Multiplies each drop's length, so a shower streaks less. */
  length: number;
  /** Odds a drop that lands throws up a splash. */
  splashChance: number;
  /** How wet the ground looks. */
  puddleOpacity: number;
};

const RAIN_PROFILES: Record<'rain' | 'storm', RainProfile> = {
  rain: {
    density: 0.26,
    tilt: -0.06,
    color: 0xd6ecff,
    opacity: 0.5,
    metalness: 0.1,
    roughness: 0.45,
    length: 0.7,
    splashChance: 0.12,
    puddleOpacity: 0.45
  },
  storm: {
    density: 1,
    tilt: -0.3,
    color: 0x88ccff,
    opacity: 0.8,
    metalness: 0.9,
    roughness: 0.1,
    length: 1,
    splashChance: 0.28,
    puddleOpacity: 0.95
  }
};

/** A parcel to leave dry, with its radius pre-squared for the scan. */
type PuddleKeepOut = { x: number; z: number; radiusSquared: number };

/**
 * How far from the camera a puddle still ripples, in voxels.
 *
 * Matches the rain field's own reach, so water moves exactly where drops are falling. Beyond it a
 * puddle is still drawn — it is one instanced mesh either way — it simply holds still, which at
 * that distance is indistinguishable from a wave six thousandths of a unit tall.
 */
const PUDDLE_RIPPLE_RANGE = 55;

/**
 * The half-width of the column of falling weather, in world units.
 *
 * `BASE` is the tuning everything else was authored against — drop counts, speeds and densities all
 * read as rain at this size — so it is also the floor. Standing in a yard looks exactly as it did.
 *
 * It has to grow, though. The column used to be this size at every zoom: a fixed 97-voxel box over
 * a world 345 voxels across, which from the ordinary camera height covered less than half the
 * screen and read as a rain shower parked over the town square. The column now widens to whatever
 * the camera can see, and the drop count rises with its *area* so the rain does not thin out as it
 * spreads. `MAX` is roughly the whole of a guild's ground, past which there is nothing to rain on.
 */
const BASE_RANGE = 55 * VOXEL_SIZE;

/**
 * A ceiling on the column regardless of the view, for a guild whose world is enormous. Past the
 * point where the drop budget runs out the rain thins rather than widening further, which is the
 * better of the two failure modes: thin rain over everything still reads as weather, where a dense
 * square of rain over one corner reads as a bug — which is exactly what it was.
 */
const HARD_MAX_RANGE = 200 * VOXEL_SIZE;

export class WeatherParticleSystem {
  private rainMesh: THREE.InstancedMesh;
  private rainMaterial: THREE.MeshStandardMaterial;
  private snowMesh: THREE.InstancedMesh;
  private splashMesh: THREE.InstancedMesh;

  /**
   * Drops in the air at `BASE_RANGE`, before the profile's density. The look of a shower versus a
   * downpour is tuned against these, so they stay put; only the capacity above them grew.
   */
  private baseRain = 650;
  private baseSnow = 500;

  /**
   * Instance capacity, sized for the widest column at the heaviest weather. One instanced mesh
   * either way, so what this buys is headroom to keep the density constant as the view widens
   * rather than any extra draw calls.
   */
  private maxRain = 6_800;
  private maxSnow = 5_200;
  private maxSplashes = 80;

  private rainParticles: {
    x: number;
    y: number;
    z: number;
    speedY: number;
    length: number;
  }[] = [];

  private snowParticles: {
    x: number;
    y: number;
    z: number;
    speedY: number;
    swaySeed: number;
    rotation: number;
    scale: number;
  }[] = [];

  private splashParticles: {
    x: number;
    y: number;
    z: number;
    life: number;
    maxLife: number;
    vx: number;
    vy: number;
    vz: number;
  }[] = [];

  // 3D Lightning System
  private lightningBolts: THREE.Group[] = [];
  private activeLightningIndex = -1;
  private lightningTimer = 0;
  private isLightningFlashing = false;
  public lightningIntensityOffset = 0;

  // Voxel Puddles System
  private puddleMesh: THREE.InstancedMesh;
  private puddleRaindropMesh: THREE.InstancedMesh;
  private puddleRippleMesh: THREE.InstancedMesh;
  private puddleMaterial: THREE.MeshStandardMaterial;

  private heightMap = new Map<string, number>();

  /** The extent of the ground the scan was given, so puddles cover the world rather than a box. */
  private groundMinX = 0;
  private groundMaxX = 0;
  private groundMinZ = 0;
  private groundMaxZ = 0;
  /** Half the world's width in world units, which is as wide as weather ever needs to fall. */
  private groundRadius = HARD_MAX_RANGE;

  private maxPuddleRaindrops = 60;
  private maxPuddleRipples = 120;
  /**
   * Instance capacity for puddle tiles; the ground decides how many are actually used.
   *
   * Sized for a whole world rather than the authored village. They are one instanced mesh, so this
   * is one draw call at any count — what the number really buys is the density of puddles per acre
   * once they are spread across every parcel and lane, and the per-frame cost of animating them is
   * held flat separately by only rippling the ones near the camera.
   */
  private maxPuddleTiles = 4_800;

  // Discrete Voxel Puddle Tile Clusters (grid-aligned x, y, z voxel coordinates)
  private puddleTiles: { x: number; y: number; z: number; baseWaveOffset: number }[] = [];

  /**
   * Whether the puddles are currently standing on the ground rather than parked out of sight.
   *
   * The ripple only ever writes the tiles near the camera, so showing and hiding the rest has to
   * happen on the turn between wet and dry — a state, not something recomputed every frame.
   */
  private puddlesShown = false;

  private puddleRaindropParticles: {
    x: number;
    y: number;
    z: number;
    baseY: number;
    vy: number;
    life: number;
    maxLife: number;
  }[] = [];

  private puddleRippleParticles: {
    x: number;
    y: number;
    z: number;
    radius: number;
    maxRadius: number;
    life: number;
    maxLife: number;
  }[] = [];

  private dummy = new THREE.Object3D();
  private scratchColor = new THREE.Color();
  private currentWeather: WeatherType = 'clear';

  constructor(scene: THREE.Scene) {
    // 1. Rain Geometry & Material (slender glossy raindrops)
    const rainGeo = new THREE.BoxGeometry(VOXEL_SIZE * 0.1, VOXEL_SIZE * 1.1, VOXEL_SIZE * 0.1);
    const rainMat = new THREE.MeshStandardMaterial({
      color: RAIN_PROFILES.storm.color,
      roughness: RAIN_PROFILES.storm.roughness,
      metalness: RAIN_PROFILES.storm.metalness,
      transparent: true,
      opacity: RAIN_PROFILES.storm.opacity,
    });
    this.rainMaterial = rainMat;
    this.rainMesh = new THREE.InstancedMesh(rainGeo, rainMat, this.maxRain);
    this.rainMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.rainMesh.castShadow = false;
    this.rainMesh.receiveShadow = false;

    // 2. Snow Geometry & Material (soft square 3D snowflakes)
    const snowGeo = new THREE.BoxGeometry(VOXEL_SIZE * 0.28, VOXEL_SIZE * 0.28, VOXEL_SIZE * 0.28);
    const snowMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive: 0x88aacc,
      emissiveIntensity: 0.4,
      roughness: 0.9,
      metalness: 0.1,
      transparent: true,
      opacity: 0.9,
    });
    this.snowMesh = new THREE.InstancedMesh(snowGeo, snowMat, this.maxSnow);
    this.snowMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.snowMesh.castShadow = false;
    this.snowMesh.receiveShadow = false;

    // 3. Splash Geometry (water droplet impacts)
    const splashGeo = new THREE.BoxGeometry(VOXEL_SIZE * 0.16, VOXEL_SIZE * 0.16, VOXEL_SIZE * 0.16);
    const splashMat = new THREE.MeshStandardMaterial({
      color: 0xbbddff,
      transparent: true,
      opacity: 0.85,
    });
    this.splashMesh = new THREE.InstancedMesh(splashGeo, splashMat, this.maxSplashes);
    this.splashMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    // 4. Voxel Puddles & Raindrop Ripples (Shimmering reflective blocky water pools)
    // Where they go is not known until the terrain arrives; see setTerrainVoxels.
    const puddleGeo = new THREE.BoxGeometry(VOXEL_SIZE * 0.98, VOXEL_SIZE * 0.08, VOXEL_SIZE * 0.98);
    this.puddleMaterial = new THREE.MeshStandardMaterial({
      color: 0x3a88d8,
      roughness: 0.04,
      metalness: 0.85,
      emissive: 0x113e66,
      emissiveIntensity: 0.35,
      transparent: true,
      opacity: 0.0,
      depthWrite: false,
    });
    this.puddleMesh = new THREE.InstancedMesh(puddleGeo, this.puddleMaterial, this.maxPuddleTiles);
    this.puddleMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.puddleMesh.receiveShadow = true;

    // Nothing is standing anywhere until the ground has been read.
    for (let i = 0; i < this.maxPuddleTiles; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.puddleMesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.puddleMesh.instanceMatrix.needsUpdate = true;

    // Voxel Raindrops in Puddles (mini bouncing 3D voxel water droplets)
    const puddleDropGeo = new THREE.BoxGeometry(VOXEL_SIZE * 0.18, VOXEL_SIZE * 0.18, VOXEL_SIZE * 0.18);
    const puddleDropMat = new THREE.MeshStandardMaterial({
      color: 0x88ddff,
      emissive: 0x2288cc,
      emissiveIntensity: 0.5,
      roughness: 0.1,
      transparent: true,
      opacity: 0.9,
    });
    this.puddleRaindropMesh = new THREE.InstancedMesh(puddleDropGeo, puddleDropMat, this.maxPuddleRaindrops);
    this.puddleRaindropMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    // Voxel Ripples in Puddles (expanding square 3D voxel ripple rings)
    const puddleRippleGeo = new THREE.BoxGeometry(VOXEL_SIZE * 0.14, VOXEL_SIZE * 0.04, VOXEL_SIZE * 0.14);
    const puddleRippleMat = new THREE.MeshStandardMaterial({
      color: 0xaaddff,
      transparent: true,
      opacity: 0.85,
      roughness: 0.1,
    });
    this.puddleRippleMesh = new THREE.InstancedMesh(puddleRippleGeo, puddleRippleMat, this.maxPuddleRipples);
    this.puddleRippleMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    // 5. Build 3D Voxel Lightning Bolts in Sky
    for (let b = 0; b < 3; b++) {
      const boltGroup = this.createLightningBoltMesh(b);
      boltGroup.visible = false;
      this.lightningBolts.push(boltGroup);
      scene.add(boltGroup);
    }

    // three.js caches an InstancedMesh bounding sphere from the instance matrices on
    // first render and setMatrixAt never invalidates it. Every particle below is parked
    // at y = -999 at that point, so the cached sphere sits far under the world and the
    // whole system is frustum-culled for the lifetime of the scene — weather renders
    // nothing at all.
    //
    // The puddles were left out of this on the reasoning that they sit on the terrain, which
    // is true of where they end up and not of where they start: they are parked with everything
    // else until the ground has been read, and parked again whenever the weather is dry. The
    // sphere therefore cached at (0, -999, 0) with radius 0 and the puddles were culled from
    // every angle that looks at the ground — they had never once been drawn.
    this.rainMesh.frustumCulled = false;
    this.snowMesh.frustumCulled = false;
    this.splashMesh.frustumCulled = false;
    this.puddleMesh.frustumCulled = false;
    this.puddleRaindropMesh.frustumCulled = false;
    this.puddleRippleMesh.frustumCulled = false;

    // Initialize all particle meshes offscreen
    for (let i = 0; i < this.maxRain; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.rainMesh.setMatrixAt(i, this.dummy.matrix);
    }
    for (let i = 0; i < this.maxSnow; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.snowMesh.setMatrixAt(i, this.dummy.matrix);
    }
    for (let i = 0; i < this.maxSplashes; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.splashMesh.setMatrixAt(i, this.dummy.matrix);
    }
    for (let i = 0; i < this.maxPuddleRaindrops; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.puddleRaindropMesh.setMatrixAt(i, this.dummy.matrix);
    }
    for (let i = 0; i < this.maxPuddleRipples; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.puddleRippleMesh.setMatrixAt(i, this.dummy.matrix);
    }

    this.rainMesh.instanceMatrix.needsUpdate = true;
    this.snowMesh.instanceMatrix.needsUpdate = true;
    this.splashMesh.instanceMatrix.needsUpdate = true;
    this.puddleRaindropMesh.instanceMatrix.needsUpdate = true;
    this.puddleRippleMesh.instanceMatrix.needsUpdate = true;

    scene.add(this.rainMesh);
    scene.add(this.snowMesh);
    scene.add(this.splashMesh);
    scene.add(this.puddleMesh);
    scene.add(this.puddleRaindropMesh);
    scene.add(this.puddleRippleMesh);

    // Populate initial particle pools
    this.initParticles();
  }

  private createLightningBoltMesh(variant: number): THREE.Group {
    const group = new THREE.Group();
    const geo = new THREE.BoxGeometry(VOXEL_SIZE * 0.5, VOXEL_SIZE * 1.3, VOXEL_SIZE * 0.5);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xeeffff,
      toneMapped: false,
    });

    // Preset positions across the sky / mountains
    const skyPositions = [
      { x: -5, z: -15 },
      { x: 18, z: 8 },
      { x: -22, z: 20 },
    ];
    const basePos = skyPositions[variant % skyPositions.length]!;

    let curX = basePos.x * VOXEL_SIZE;
    let curZ = basePos.z * VOXEL_SIZE;
    let curY = 42 * VOXEL_SIZE;

    while (curY > 6 * VOXEL_SIZE) {
      const vox = new THREE.Mesh(geo, mat);
      vox.position.set(curX, curY, curZ);
      group.add(vox);

      curY -= (0.9 + Math.random() * 0.6) * VOXEL_SIZE;
      curX += (Math.random() - 0.48) * 2.4 * VOXEL_SIZE;
      curZ += (Math.random() - 0.5) * 2.4 * VOXEL_SIZE;

      // Branching bolts
      if (Math.random() < 0.22) {
        let branchX = curX;
        let branchY = curY;
        let branchZ = curZ;
        for (let k = 0; k < 5; k++) {
          const branchVox = new THREE.Mesh(geo, mat);
          branchVox.scale.set(0.65, 0.65, 0.65);
          branchVox.position.set(branchX, branchY, branchZ);
          group.add(branchVox);
          branchY -= 0.7 * VOXEL_SIZE;
          branchX += (Math.random() - 0.3) * 1.8 * VOXEL_SIZE;
          branchZ += (Math.random() - 0.5) * 1.8 * VOXEL_SIZE;
        }
      }
    }

    return group;
  }

  private initParticles() {
    const range = 52 * VOXEL_SIZE;

    // Rain
    for (let i = 0; i < this.maxRain; i++) {
      this.rainParticles.push({
        x: (Math.random() - 0.5) * range * 2,
        y: Math.random() * 45 * VOXEL_SIZE,
        z: (Math.random() - 0.5) * range * 2,
        speedY: (32 + Math.random() * 18) * VOXEL_SIZE,
        length: 0.9 + Math.random() * 0.7,
      });
    }

    // Snow
    for (let i = 0; i < this.maxSnow; i++) {
      this.snowParticles.push({
        x: (Math.random() - 0.5) * range * 2,
        y: Math.random() * 45 * VOXEL_SIZE,
        z: (Math.random() - 0.5) * range * 2,
        speedY: (3.5 + Math.random() * 2.8) * VOXEL_SIZE,
        swaySeed: Math.random() * 100,
        rotation: Math.random() * Math.PI * 2,
        scale: 0.75 + Math.random() * 0.6,
      });
    }
  }

  /**
   * Finds ground for the puddles to sit in.
   *
   * Upstream this was a hand-written list of coordinates — the well square, the watermill lane,
   * the doorstep of a particular cottage — which works for one authored village and nowhere
   * else. Every guild's world is generated from its own seed here, so those spots would land in
   * the river or inside somebody's hill. Instead the ground picks the puddles: a tile qualifies
   * when it and its neighbours are all land at the same height, which is exactly the flat,
   * trodden ground where water would actually stand.
   *
   * The scan is ordered, not random, so a guild's puddles fall in the same places every visit.
   *
   * It covers the whole world, not the authored village. It used to stop at 44 voxels, which was
   * 0.3% of a guild's ground — and worse, the budget was spent in scan order, so it ran out four
   * columns in and left even the village itself mostly dry. Candidates are now gathered everywhere
   * first and thinned evenly afterwards, so the density is the same at the far edge as at the well.
   */
  private scatterPuddles(keepOut: PuddleKeepOut[]) {
    const patterns: { dx: number; dz: number }[][] = [
      [{ dx: 0, dz: 0 }, { dx: 1, dz: 0 }, { dx: 0, dz: 1 }, { dx: 1, dz: 1 }],
      [{ dx: 0, dz: 0 }, { dx: 1, dz: 0 }, { dx: 2, dz: 0 }, { dx: 1, dz: 1 }],
      [{ dx: 0, dz: 0 }, { dx: -1, dz: 0 }, { dx: 0, dz: 1 }, { dx: 1, dz: 0 }, { dx: 0, dz: -1 }]
    ];

    const flatAt = (x: number, z: number): number | null => {
      const height = this.heightMap.get(`${x},${z}`);
      if (height === undefined) return null;
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        if (this.heightMap.get(`${x + dx},${z + dz}`) !== height) return null;
      }
      return height;
    };

    /**
     * Members' parcels are levelled flat, which is exactly what the flatness test looks for, so
     * without this every yard in the guild would be the wettest ground in the world. Squared
     * radii, because this runs for every candidate against every parcel.
     */
    const inSomebodysGarden = (x: number, z: number): boolean =>
      keepOut.some((plot) => {
        const dx = x - plot.x;
        const dz = z - plot.z;
        return dx * dx + dz * dz < plot.radiusSquared;
      });

    this.puddleTiles = [];
    const taken = new Set<string>();
    const stride = 7;
    let pattern = 0;

    // Gather everywhere the ground actually reaches, rather than a fixed box around the origin.
    const clusters: { x: number; y: number; z: number }[][] = [];
    const scan = { candidates: 0, clusters: 0, blockedByParcel: 0, notFlat: 0, tooSmall: 0 };
    for (let x = this.groundMinX; x <= this.groundMaxX; x += stride) {
      for (let z = this.groundMinZ; z <= this.groundMaxZ; z += stride) {
        scan.candidates += 1;
        if (inSomebodysGarden(x, z)) { scan.blockedByParcel += 1; continue; }
        const height = flatAt(x, z);
        if (height === null) { scan.notFlat += 1; continue; }

        const shape = patterns[pattern % patterns.length]!;
        pattern += 1;

        const tiles = shape
          .map((offset) => ({ x: x + offset.dx, z: z + offset.dz }))
          .filter((tile) =>
            !taken.has(`${tile.x},${tile.z}`) &&
            flatAt(tile.x, tile.z) === height &&
            !inSomebodysGarden(tile.x, tile.z)
          );
        if (tiles.length < 3) { scan.tooSmall += 1; continue; }

        for (const tile of tiles) taken.add(`${tile.x},${tile.z}`);
        clusters.push(tiles.map((tile) => ({ x: tile.x, y: height, z: tile.z })));
        scan.clusters += 1;
      }
    }
    this.lastScan = scan;

    // Thin evenly if the world offers more than the budget, so what survives is spread rather
    // than whichever corner happened to be scanned first.
    const total = clusters.reduce((sum, cluster) => sum + cluster.length, 0);
    const keepEvery = total > this.maxPuddleTiles ? total / this.maxPuddleTiles : 1;
    let seen = 0;
    let kept = 0;

    for (const cluster of clusters) {
      // Whole clusters, not individual tiles — half a puddle is a puzzle, not a puddle.
      const takeThis = Math.floor(seen / keepEvery) >= kept;
      seen += cluster.length;
      if (!takeThis) continue;
      if (this.puddleTiles.length + cluster.length > this.maxPuddleTiles) continue;
      kept += cluster.length;

      for (const tile of cluster) {
        this.puddleTiles.push({
          x: tile.x,
          y: tile.y,
          z: tile.z,
          // Offset from the tile rather than at random, so the ripple never resets on rebuild.
          baseWaveOffset: ((tile.x * 7 + tile.z * 13) % 32) / 32 * Math.PI * 2
        });
      }
    }

    // Spatially ordered, so that the ripple window below — which is a circle around the camera —
    // lands on a near-contiguous run of instances and can upload that slice instead of the lot.
    const cell = 24;
    this.puddleTiles.sort((a, b) => {
      const cellA = Math.floor(a.x / cell) * 4096 + Math.floor(a.z / cell);
      const cellB = Math.floor(b.x / cell) * 4096 + Math.floor(b.z / cell);
      return cellA - cellB;
    });
  }

  /**
   * @param keepOut Parcels to leave dry, as centres and radii in voxels.
   */
  /** Stands every puddle on the ground at rest, with no wave. Runs on the turn into rain. */
  private showAllPuddles() {
    for (let i = 0; i < this.maxPuddleTiles; i++) {
      const tile = this.puddleTiles[i];
      if (tile) {
        this.dummy.position.set(
          tile.x * VOXEL_SIZE,
          (tile.y + 0.5) * VOXEL_SIZE + 0.05 * VOXEL_SIZE,
          tile.z * VOXEL_SIZE
        );
        this.dummy.scale.set(1, 1, 1);
      } else {
        this.dummy.position.set(0, -999, 0);
        this.dummy.scale.set(0, 0, 0);
      }
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.puddleMesh.setMatrixAt(i, this.dummy.matrix);
    }
    // A whole-buffer upload, which is what this one-off is for; the ripple uploads a slice.
    this.puddleMesh.instanceMatrix.clearUpdateRanges();
    this.puddleMesh.instanceMatrix.needsUpdate = true;
  }

  /** What the puddle scan actually found, for checking coverage from the console. */
  public get puddleStats() {
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const tile of this.puddleTiles) {
      if (tile.x < minX) minX = tile.x;
      if (tile.x > maxX) maxX = tile.x;
      if (tile.z < minZ) minZ = tile.z;
      if (tile.z > maxZ) maxZ = tile.z;
    }
    return {
      tiles: this.puddleTiles.length,
      capacity: this.maxPuddleTiles,
      groundSpan: [this.groundMinX, this.groundMaxX, this.groundMinZ, this.groundMaxZ],
      groundCells: this.heightMap.size,
      puddleSpan: [minX, maxX, minZ, maxZ],
      scan: this.lastScan
    };
  }

  private lastScan = { candidates: 0, clusters: 0, blockedByParcel: 0, notFlat: 0, tooSmall: 0 };

  public setTerrainVoxels(
    terrainVoxels: { x: number; y: number; z: number }[],
    keepOut: { x: number; z: number; radius: number }[] = []
  ) {
    this.heightMap.clear();
    this.groundMinX = Infinity;
    this.groundMaxX = -Infinity;
    this.groundMinZ = Infinity;
    this.groundMaxZ = -Infinity;

    for (const v of terrainVoxels) {
      const key = `${v.x},${v.z}`;
      const currentMax = this.heightMap.get(key);
      if (currentMax === undefined || v.y > currentMax) {
        this.heightMap.set(key, v.y);
      }
      if (v.x < this.groundMinX) this.groundMinX = v.x;
      if (v.x > this.groundMaxX) this.groundMaxX = v.x;
      if (v.z < this.groundMinZ) this.groundMinZ = v.z;
      if (v.z > this.groundMaxZ) this.groundMaxZ = v.z;
    }

    if (!Number.isFinite(this.groundMinX)) {
      this.groundMinX = this.groundMaxX = this.groundMinZ = this.groundMaxZ = 0;
    }

    // Measured from the origin rather than from the ground's own centre, because the weather is
    // aimed at wherever the member is looking and the world is laid out around the town square.
    this.groundRadius = Math.max(
      BASE_RANGE,
      Math.max(
        Math.abs(this.groundMinX), Math.abs(this.groundMaxX),
        Math.abs(this.groundMinZ), Math.abs(this.groundMaxZ)
      ) * VOXEL_SIZE
    );

    this.scatterPuddles(
      keepOut.map((plot) => ({ x: plot.x, z: plot.z, radiusSquared: plot.radius * plot.radius }))
    );

    // Lay the tiles on the ground the scan chose, and park the unused capacity out of sight.
    this.showAllPuddles();
    this.puddlesShown = true;
  }

  /**
   * @param focus Where the weather should be falling — the point the camera is looking at.
   *
   * Upstream the drops rained on a fixed box around the origin, which covered a village fifty
   * voxels across. A guild's world is several times that and members spend their time out on
   * their own parcels, so a world-centred field would rain on the town square while somebody
   * stood in dry sunshine at the edge. The field follows the view instead: drops respawn around
   * `focus`, and any that fall outside it are recycled early.
   */
  public update(
    weather: WeatherType,
    delta: number,
    totalTime: number,
    focus: THREE.Vector3,
    viewRadius: number = BASE_RANGE
  ) {
    this.currentWeather = weather;

    // Never wider than the ground itself — past the world's edge there is nothing to rain on, and
    // the drops would be spent on empty sky instead of on the village.
    const ceiling = Math.min(HARD_MAX_RANGE, Math.max(BASE_RANGE, this.groundRadius));
    const range = Math.min(ceiling, Math.max(BASE_RANGE, viewRadius));
    // Drops per unit of ground, held constant as the column widens. Squared because the column
    // grows in two dimensions — scaling the count linearly would still thin the rain out.
    const spread = (range / BASE_RANGE) ** 2;
    const isStorm = weather === 'storm';
    const isRain = weather === 'rain' || isStorm;
    const isSnow = weather === 'snow';

    // A shower and a downpour are the same drops under different settings. The material eases
    // between the two rather than switching, so passing from rain into storm darkens and thickens
    // the rain instead of replacing it.
    const profile = RAIN_PROFILES[isStorm ? 'storm' : 'rain'];
    if (isRain) {
      const ease = Math.min(1, delta * 2.5);
      this.rainMaterial.color.lerp(this.scratchColor.setHex(profile.color), ease);
      this.rainMaterial.opacity = THREE.MathUtils.lerp(this.rainMaterial.opacity, profile.opacity, ease);
      this.rainMaterial.metalness = THREE.MathUtils.lerp(this.rainMaterial.metalness, profile.metalness, ease);
      this.rainMaterial.roughness = THREE.MathUtils.lerp(this.rainMaterial.roughness, profile.roughness, ease);
    }

    // 1. Handle Voxel Puddles Opacity & Wave Surface
    const targetPuddleOpacity = isRain ? profile.puddleOpacity : 0.0;
    this.puddleMaterial.opacity = THREE.MathUtils.lerp(this.puddleMaterial.opacity, targetPuddleOpacity, delta * 2.5);

    if (this.puddleMaterial.opacity > 0.05 && this.puddleTiles.length > 0) {
      // Everything is parked while it is dry, so the whole set has to be stood back up once when
      // the rain starts. The ripple below only ever touches tiles near the camera, so without
      // this every puddle beyond that first window would stay parked for the rest of the storm.
      if (!this.puddlesShown) {
        this.showAllPuddles();
        this.puddlesShown = true;
      }

      // Ripple only what is near enough to see rippling. Rewriting every tile in the guild each
      // frame cost nothing in draw calls — it is one instanced mesh — but re-uploaded the whole
      // instance buffer sixty times a second to animate thousands of puddles nobody was looking at.
      const focusX = focus.x / VOXEL_SIZE;
      const focusZ = focus.z / VOXEL_SIZE;
      const rippleRangeSquared = PUDDLE_RIPPLE_RANGE * PUDDLE_RIPPLE_RANGE;
      let firstTouched = -1;
      let lastTouched = -1;

      for (let i = 0; i < this.puddleTiles.length; i++) {
        const tile = this.puddleTiles[i]!;
        const dx = tile.x - focusX;
        const dz = tile.z - focusZ;
        if (dx * dx + dz * dz > rippleRangeSquared) continue;

        const wave = Math.sin(totalTime * 5.5 + tile.baseWaveOffset) * 0.012 * VOXEL_SIZE;
        const tileY = (tile.y + 0.5) * VOXEL_SIZE + 0.05 * VOXEL_SIZE + wave;
        this.dummy.position.set(tile.x * VOXEL_SIZE, tileY, tile.z * VOXEL_SIZE);
        this.dummy.rotation.set(0, 0, 0);
        this.dummy.scale.set(1.0, 1.0, 1.0);
        this.dummy.updateMatrix();
        this.puddleMesh.setMatrixAt(i, this.dummy.matrix);

        if (firstTouched < 0) firstTouched = i;
        lastTouched = i;
      }

      // Tiles are stored in spatial order, so the window is a near-contiguous run and the upload
      // is a slice of the buffer rather than all of it.
      if (firstTouched >= 0) {
        this.puddleMesh.instanceMatrix.clearUpdateRanges();
        this.puddleMesh.instanceMatrix.addUpdateRange(
          firstTouched * 16,
          (lastTouched - firstTouched + 1) * 16
        );
        this.puddleMesh.instanceMatrix.needsUpdate = true;
      }

      // Spawn Voxel Raindrops & Ripples in Puddles during Rain/Storm.
      //
      // Drawn from the window above rather than from every puddle in the guild: spread across a
      // whole world, a uniform pick would spend the sixty-drop budget almost entirely on water
      // the member cannot see. The range is only near-contiguous, so a pick that lands outside it
      // is dropped rather than corrected — the spawn is already a dice roll each frame.
      const nearby = firstTouched < 0
        ? null
        : this.puddleTiles[firstTouched + Math.floor(Math.random() * (lastTouched - firstTouched + 1))]!;
      const inWindow = nearby !== null &&
        (nearby.x - focusX) ** 2 + (nearby.z - focusZ) ** 2 <= rippleRangeSquared;

      if (inWindow && Math.random() < (isStorm ? 0.65 : 0.40)) {
        const tile = nearby!;
        const tileY = (tile.y + 0.5) * VOXEL_SIZE + 0.08 * VOXEL_SIZE;

        const rx = tile.x * VOXEL_SIZE + (Math.random() - 0.5) * 0.7 * VOXEL_SIZE;
        const rz = tile.z * VOXEL_SIZE + (Math.random() - 0.5) * 0.7 * VOXEL_SIZE;

        // Spawn Bouncing Voxel Raindrop
        if (this.puddleRaindropParticles.length < this.maxPuddleRaindrops) {
          this.puddleRaindropParticles.push({
            x: rx,
            y: tileY + 0.05 * VOXEL_SIZE,
            z: rz,
            baseY: tileY,
            vy: (1.5 + Math.random() * 2.0) * VOXEL_SIZE,
            life: 0,
            maxLife: 0.18 + Math.random() * 0.14,
          });
        }

        // Spawn Expanding Voxel Ripple Ring
        if (this.puddleRippleParticles.length < this.maxPuddleRipples) {
          this.puddleRippleParticles.push({
            x: rx,
            y: tileY + 0.02 * VOXEL_SIZE,
            z: rz,
            radius: 0.08 * VOXEL_SIZE,
            maxRadius: (0.35 + Math.random() * 0.25) * VOXEL_SIZE,
            life: 0,
            maxLife: 0.25 + Math.random() * 0.18,
          });
        }
      }
    } else if (this.puddlesShown) {
      // Hide puddles when clear/snow. Once on the turn, not every frame — the set is the whole
      // world's worth now, and re-parking already-parked water sixty times a second is a full
      // buffer upload to say nothing has changed.
      this.puddlesShown = false;
      this.puddleTiles.forEach((tile, i) => {
        this.dummy.position.set(0, -999, 0);
        this.dummy.scale.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.puddleMesh.setMatrixAt(i, this.dummy.matrix);
      });
      this.puddleMesh.instanceMatrix.needsUpdate = true;
    }

    // 2. Update Puddle Raindrop particles
    for (let i = this.puddleRaindropParticles.length - 1; i >= 0; i--) {
      const drop = this.puddleRaindropParticles[i]!;
      drop.life += delta;
      drop.y += drop.vy * delta;
      drop.vy -= 12.0 * VOXEL_SIZE * delta;

      const progress = drop.life / drop.maxLife;
      if (progress >= 1.0 || drop.y < drop.baseY) {
        this.puddleRaindropParticles.splice(i, 1);
        continue;
      }

      const s = (1 - progress) * 0.9;
      this.dummy.position.set(drop.x, drop.y, drop.z);
      this.dummy.scale.set(s, s, s);
      this.dummy.updateMatrix();
      this.puddleRaindropMesh.setMatrixAt(i, this.dummy.matrix);
    }
    for (let i = this.puddleRaindropParticles.length; i < this.maxPuddleRaindrops; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.puddleRaindropMesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.puddleRaindropMesh.instanceMatrix.needsUpdate = true;

    // 3. Update Voxel Square Ripple particles
    let rippleInstanceIndex = 0;
    for (let i = this.puddleRippleParticles.length - 1; i >= 0; i--) {
      const rip = this.puddleRippleParticles[i]!;
      rip.life += delta;
      const progress = rip.life / rip.maxLife;

      if (progress >= 1.0) {
        this.puddleRippleParticles.splice(i, 1);
        continue;
      }

      const curRadius = THREE.MathUtils.lerp(rip.radius, rip.maxRadius, progress);
      const scale = (1 - progress) * 0.85;

      // 4 Voxel Corner/Edge Blocks forming an expanding square ripple ring on the puddle
      const offsets = [
        { dx: curRadius, dz: curRadius },
        { dx: -curRadius, dz: curRadius },
        { dx: curRadius, dz: -curRadius },
        { dx: -curRadius, dz: -curRadius },
      ];

      offsets.forEach(off => {
        if (rippleInstanceIndex < this.maxPuddleRipples) {
          this.dummy.position.set(rip.x + off.dx, rip.y, rip.z + off.dz);
          this.dummy.scale.set(scale, scale, scale);
          this.dummy.updateMatrix();
          this.puddleRippleMesh.setMatrixAt(rippleInstanceIndex, this.dummy.matrix);
          rippleInstanceIndex++;
        }
      });
    }
    for (let i = rippleInstanceIndex; i < this.maxPuddleRipples; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.puddleRippleMesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.puddleRippleMesh.instanceMatrix.needsUpdate = true;

    // Handle Rain Drops
    const activeRainCount = isRain
      ? Math.min(this.maxRain, Math.round(this.baseRain * profile.density * spread))
      : 0;
    const windTilt = isRain ? profile.tilt : 0;

    for (let i = 0; i < this.maxRain; i++) {
      if (i < activeRainCount) {
        const p = this.rainParticles[i]!;
        p.y -= p.speedY * delta;
        p.x += windTilt * p.speedY * delta;

        // Ground collision reset
        const ground = this.groundAt(p.x, p.z);
        const strayed = Math.abs(p.x - focus.x) > range || Math.abs(p.z - focus.z) > range;
        if (p.y <= ground || strayed) {
          // Chance to spawn a ground splash
          if (!strayed && this.splashParticles.length < this.maxSplashes && Math.random() < profile.splashChance) {
            this.spawnSplash(p.x, ground + 0.1 * VOXEL_SIZE, p.z);
          }

          p.y = focus.y + 42 * VOXEL_SIZE + Math.random() * 5 * VOXEL_SIZE;
          p.x = focus.x + (Math.random() - 0.5) * range * 1.8;
          p.z = focus.z + (Math.random() - 0.5) * range * 1.8;
        }

        this.dummy.position.set(p.x, p.y, p.z);
        this.dummy.rotation.set(0, 0, windTilt);
        this.dummy.scale.set(1, p.length * profile.length, 1);
        this.dummy.updateMatrix();
        this.rainMesh.setMatrixAt(i, this.dummy.matrix);
      } else {
        this.dummy.position.set(0, -999, 0);
        this.dummy.scale.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.rainMesh.setMatrixAt(i, this.dummy.matrix);
      }
    }
    this.rainMesh.instanceMatrix.needsUpdate = true;

    // Handle Snow Particles
    const activeSnowCount = isSnow
      ? Math.min(this.maxSnow, Math.round(this.baseSnow * spread))
      : 0;
    const snowWindX = isSnow ? 0.9 * VOXEL_SIZE : 0;

    for (let i = 0; i < this.maxSnow; i++) {
      if (i < activeSnowCount) {
        const p = this.snowParticles[i]!;
        p.y -= p.speedY * delta;

        // Gentle floating sway
        const swayX = Math.sin(totalTime * 2.2 + p.swaySeed) * 1.3 * VOXEL_SIZE;
        const swayZ = Math.cos(totalTime * 1.8 + p.swaySeed) * 1.3 * VOXEL_SIZE;

        p.x += (swayX + snowWindX) * delta;
        p.z += swayZ * delta;
        p.rotation += 1.4 * delta;

        if (
          p.y <= this.groundAt(p.x, p.z) ||
          Math.abs(p.x - focus.x) > range ||
          Math.abs(p.z - focus.z) > range
        ) {
          p.y = focus.y + 42 * VOXEL_SIZE + Math.random() * 5 * VOXEL_SIZE;
          p.x = focus.x + (Math.random() - 0.5) * range * 1.8;
          p.z = focus.z + (Math.random() - 0.5) * range * 1.8;
        }

        this.dummy.position.set(p.x, p.y, p.z);
        this.dummy.rotation.set(0, p.rotation, 0);
        this.dummy.scale.set(p.scale, p.scale, p.scale);
        this.dummy.updateMatrix();
        this.snowMesh.setMatrixAt(i, this.dummy.matrix);
      } else {
        this.dummy.position.set(0, -999, 0);
        this.dummy.scale.set(0, 0, 0);
        this.dummy.updateMatrix();
        this.snowMesh.setMatrixAt(i, this.dummy.matrix);
      }
    }
    this.snowMesh.instanceMatrix.needsUpdate = true;

    // Handle Splashes
    for (let i = this.splashParticles.length - 1; i >= 0; i--) {
      const sp = this.splashParticles[i]!;
      sp.life += delta;
      const progress = sp.life / sp.maxLife;

      sp.x += sp.vx * delta;
      sp.y += sp.vy * delta;
      sp.z += sp.vz * delta;
      sp.vy -= 9.8 * VOXEL_SIZE * delta;

      if (progress >= 1.0 || sp.y < 0) {
        this.splashParticles.splice(i, 1);
        continue;
      }

      const s = (1 - progress) * 0.85;
      this.dummy.position.set(sp.x, Math.max(0, sp.y), sp.z);
      this.dummy.scale.set(s, s, s);
      this.dummy.updateMatrix();
      this.splashMesh.setMatrixAt(i, this.dummy.matrix);
    }

    for (let i = this.splashParticles.length; i < this.maxSplashes; i++) {
      this.dummy.position.set(0, -999, 0);
      this.dummy.scale.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.splashMesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.splashMesh.instanceMatrix.needsUpdate = true;

    // Handle 3D Lightning Flash Logic during Storms
    if (isStorm) {
      this.lightningTimer += delta;

      if (this.isLightningFlashing) {
        if (this.lightningTimer > 0.18) {
          this.isLightningFlashing = false;
          this.lightningIntensityOffset = 0;
          this.lightningTimer = 0;
          if (this.activeLightningIndex >= 0) {
            this.lightningBolts[this.activeLightningIndex]!.visible = false;
            this.activeLightningIndex = -1;
          }
        } else {
          // Flicker
          const bolt = this.lightningBolts[this.activeLightningIndex];
          if (bolt) {
            bolt.visible = Math.random() > 0.15;
          }
        }
      } else if (this.lightningTimer > 4.5 + Math.random() * 6.0) {
        // Trigger 3D Lightning strike
        this.isLightningFlashing = true;
        this.lightningIntensityOffset = 4.2;
        this.lightningTimer = 0;

        this.activeLightningIndex = Math.floor(Math.random() * this.lightningBolts.length);
        const bolt = this.lightningBolts[this.activeLightningIndex];
        if (bolt) {
          bolt.visible = true;
        }
      }
    } else {
      this.isLightningFlashing = false;
      this.lightningIntensityOffset = 0;
      this.lightningTimer = 0;
      this.lightningBolts.forEach(b => b.visible = false);
    }
  }

  /**
   * The height of the ground under a point, in world units.
   *
   * Upstream every drop died at y = 0, which was true of a village sitting on the origin plane.
   * This terrain is raised and hilly, so drops would vanish through the hillsides and splash
   * somewhere underneath them. Falls back to 0 off the edge of the world, where nothing is
   * standing to watch anyway.
   */
  private groundAt(x: number, z: number): number {
    const height = this.heightMap.get(`${Math.round(x / VOXEL_SIZE)},${Math.round(z / VOXEL_SIZE)}`);
    return height === undefined ? 0 : (height + 0.5) * VOXEL_SIZE;
  }

  private spawnSplash(x: number, y: number, z: number) {
    for (let k = 0; k < 2; k++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (2.5 + Math.random() * 3.5) * VOXEL_SIZE;
      this.splashParticles.push({
        x,
        y,
        z,
        life: 0,
        maxLife: 0.16 + Math.random() * 0.12,
        vx: Math.cos(angle) * speed,
        vy: (2.8 + Math.random() * 2.2) * VOXEL_SIZE,
        vz: Math.sin(angle) * speed,
      });
    }
  }

  public destroy(scene: THREE.Scene) {
    scene.remove(this.rainMesh);
    scene.remove(this.snowMesh);
    scene.remove(this.splashMesh);
    scene.remove(this.puddleMesh);
    scene.remove(this.puddleRaindropMesh);
    scene.remove(this.puddleRippleMesh);

    this.lightningBolts.forEach(b => {
      scene.remove(b);
      b.traverse(obj => {
        if ((obj as THREE.Mesh).geometry) (obj as THREE.Mesh).geometry.dispose();
      });
    });

    this.rainMesh.geometry.dispose();
    (this.rainMesh.material as THREE.Material).dispose();

    this.snowMesh.geometry.dispose();
    (this.snowMesh.material as THREE.Material).dispose();

    this.splashMesh.geometry.dispose();
    (this.splashMesh.material as THREE.Material).dispose();

    this.puddleMesh.geometry.dispose();
    this.puddleMaterial.dispose();

    this.puddleRaindropMesh.geometry.dispose();
    (this.puddleRaindropMesh.material as THREE.Material).dispose();

    this.puddleRippleMesh.geometry.dispose();
    (this.puddleRippleMesh.material as THREE.Material).dispose();
  }
}
